import React, { useMemo, useState, useCallback, useEffect, memo } from 'react';
import { Modal, Icon, CircularProgress } from '@ann/web-components';
import { useMediaQueries } from '../../../../../utils/mediaQueries';
import { 
  ModalBodyContent, 
  FileExplorerContainer, 
  FileItemLeft, 
  FileItemRight, 
  FileName, 
  FileItemStyled, 
  LoadingContainer,
  NestedContent,
  NestedLoading,
  EmptyStateMessage
} from './AIMSDriveModal.styled';
import { useStore } from '../../../../../hooks';
import { useFolderTree } from './hooks/useFolderTree';
import { useToast } from '../../../../../components/toast';
import { attachFiletoKnowledge } from '../../services/knowledge';

// Icon configs as constants (defined outside component to avoid recreation)
const ICON_CONFIGS = {
  folder: { name: 'folder', color: 'primary-alt' },
  pdf: { name: 'description', color: 'red' },
  docx: { name: 'description', color: 'blue' },
  doc: { name: 'description', color: 'blue' },
  xlsx: { name: 'description', color: 'limeGreen' },
  xls: { name: 'description', color: 'limeGreen' },
  pptx: { name: 'description', color: 'orange' },
  ppt: { name: 'description', color: 'orange' },
  png: { name: 'image', color: 'primary-alt' },
  jpg: { name: 'image', color: 'primary-alt' },
  jpeg: { name: 'image', color: 'primary-alt' },
  default: { name: 'description', color: 'primary-alt' },
};

const getFileIcon = (type) => ICON_CONFIGS[type] || ICON_CONFIGS.default;

// ✅ OPTIMIZED: Memoized FileItem to prevent unnecessary re-renders
const FileItem = memo(({ 
  file, 
  isExpanded, 
  isSelected, 
  isLoading,
  onToggle
}) => {
  const iconConfig = getFileIcon(file.type);
  const chevronName = isExpanded ? 'chevronDown' : 'chevronRight';
  const isFolder = file.type === 'folder';
  
  // ✅ OPTIMIZED: Stable callback reference
  const handleClick = useCallback(() => {
    onToggle(file);
  }, [onToggle, file]);
  
  return (
    <FileItemStyled 
      $isActive={isSelected} 
      onClick={handleClick}
    >
      <FileItemLeft>
        <Icon
          color={iconConfig.color}
          name={iconConfig.name}
          size="medium"
        />
        <FileName title={file.name}>{file.name}</FileName>
      </FileItemLeft>
      <FileItemRight>
        {isFolder && (
          isLoading ? (
            <CircularProgress size="small" />
          ) : (
            <Icon
              color="neutral"
              name={chevronName}
              size="medium"
            />
          )
        )}
      </FileItemRight>
    </FileItemStyled>
  );
});

FileItem.displayName = 'FileItem';

const AIMSDriveModal = ({
  isOpen,
  onClose,
  knowledgeBase,
}) => {
  const { isMobile, isIpadAirPortrait, isIpadProPortrait } = useMediaQueries();
  const { drivesStore } = useStore();
  
  // State for UI interactions
  const [expandedFolders, setExpandedFolders] = useState({});
  const [selectedFiles, setSelectedFiles] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  
  const toast = useToast();
  
  // ✅ OPTIMIZED: Extract primitive for dependency
  const drivesLength = drivesStore?.drives?.length || 0;
  
  // ✅ OPTIMIZED: Only depend on primitive, access store inside
  const defaultVaultUuid = useMemo(() => {
    if (drivesLength === 0) return null;
    return drivesStore?.drives?.[0]?.id || null;
  }, [drivesLength]);
  
  // Load drives if not loaded
  useEffect(() => {
    if (!isOpen || drivesLength > 0) return;
    
    drivesStore?.fetchDrives?.("")?.catch?.(() => {});
  }, [isOpen, drivesLength]);
  
  // Use folder tree hook for all data loading logic
  const { treeData, loadingFolders, isInitialLoading, loadFolderChildren } = useFolderTree(
    defaultVaultUuid,
    isOpen
  );
  
  // Reset UI state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setExpandedFolders({});
      setSelectedFiles({});
    }
  }, [isOpen]);
  
  // ✅ OPTIMIZED: Stable handler with direct loadFolderChildren call
  const handleToggle = useCallback((file) => {
    const isFolder = file.type === 'folder';
    const itemId = file.id;
    const folderUuid = file.folder_uuid || file.id;
    
    if (isFolder) {
      setExpandedFolders(prev => {
        const isCurrentlyExpanded = !!prev[itemId];
        
        if (isCurrentlyExpanded) {
          const { [itemId]: removed, ...rest } = prev;
          return rest;
        } else {
          // Load children after state update
          loadFolderChildren(folderUuid);
          return { ...prev, [itemId]: true };
        }
      });
    } else {
      setSelectedFiles(prev => {
        const isCurrentlySelected = !!prev[itemId];
        if (isCurrentlySelected) {
          const { [itemId]: removed, ...rest } = prev;
          return rest;
        } else {
          return { ...prev, [itemId]: true };
        }
      });
    }
  }, [loadFolderChildren]);

  // ✅ OPTIMIZED: Regular function instead of useCallback (not passed to memoized child)
  const renderTree = (parentKey = 'root', level = 0) => {
    if (level > 10) return null;
    
    const parentData = treeData[parentKey];
    if (!parentData) return null;
    
    const { folders = [], documents = [] } = parentData;
    const allItems = [...folders, ...documents];
    
    if (allItems.length === 0) return null;
    
    return (
      <>
        {allItems.map((item) => {
          const isFolder = item.type === 'folder';
          const itemId = item.id;
          const folderUuid = isFolder ? (item.folder_uuid || item.id) : null;
          
          const isExpanded = isFolder && !!expandedFolders[itemId];
          const isLoading = isFolder && folderUuid && !!loadingFolders[folderUuid];
          const isSelected = !isFolder && !!selectedFiles[itemId];
          const hasChildren = isFolder && folderUuid && treeData[folderUuid];
          const childData = hasChildren ? treeData[folderUuid] : null;
          const hasContent = childData && (childData.folders?.length > 0 || childData.documents?.length > 0);
          
          return (
            <React.Fragment key={itemId}>
              <FileItem
                file={item}
                isExpanded={isExpanded}
                isSelected={isSelected}
                isLoading={isLoading}
                onToggle={handleToggle}
              />
              {isExpanded && isFolder && folderUuid && (
                <NestedContent $level={level}>
                  {isLoading && !hasChildren && (
                    <NestedLoading>
                      <CircularProgress size="small" />
                      <span>Loading...</span>
                    </NestedLoading>
                  )}
                  {hasChildren && hasContent && renderTree(folderUuid, level + 1)}
                  {hasChildren && !hasContent && !isLoading && (
                    <NestedLoading>
                      <span>Empty folder</span>
                    </NestedLoading>
                  )}
                </NestedContent>
              )}
            </React.Fragment>
          );
        })}
      </>
    );
  };

  // ✅ OPTIMIZED: Memoized modal width
  const modalWidth = useMemo(() => {
    return isMobile || isIpadAirPortrait || isIpadProPortrait ? '95%' : '600px';
  }, [isMobile, isIpadAirPortrait, isIpadProPortrait]);

  // ✅ OPTIMIZED: Memoized selected document UUIDs
  const selectedDocumentUuids = useMemo(() => {
    return Object.keys(selectedFiles);
  }, [selectedFiles]);

  const selectedCount = selectedDocumentUuids.length;

  // ✅ OPTIMIZED: Helper function to find document in tree data
  const findDocumentInTree = useCallback((documentUuid, tree = treeData, parentKey = 'root') => {
    const parentData = tree[parentKey];
    if (!parentData) return null;
    
    // Check documents in current level
    const document = parentData.documents?.find(doc => doc.id === documentUuid || doc.document_uuid === documentUuid);
    if (document) return document;
    
    // Recursively search in folders
    if (parentData.folders) {
      for (const folder of parentData.folders) {
        const folderUuid = folder.folder_uuid || folder.id;
        if (tree[folderUuid]) {
          const found = findDocumentInTree(documentUuid, tree, folderUuid);
          if (found) return found;
        }
      }
    }
    
    return null;
  }, [treeData]);

  // Handle process files click
  const handleProcessFiles = useCallback(async () => {
    if (selectedCount === 0 || isProcessing) return;
    
    setIsProcessing(true);
    
    try {
      // Get all selected documents from tree data
      const items = selectedDocumentUuids
        .map(documentUuid => {
          const document = findDocumentInTree(documentUuid);
          if (!document) return null;
          
          return {
            document_uuid: document.document_uuid || document.id,
            ...(document.file_uuid && { file_uuid: document.file_uuid }),
            ...(document.file_id && { file_id: document.file_id }),
          };
        })
        .filter(Boolean); // Remove null entries
      
      if (items.length === 0) {
        toast.error({
          title: 'Error',
          message: 'No valid documents found to attach.',
        });
        setIsProcessing(false);
        return;
      }
      
      const response = await attachFiletoKnowledge({ items });
      
      // Check if request was successful
      if (response.status_code === 200) {
        const attachedCount = response.body?.attached?.length || 0;
        toast.success({
          title: 'Success',
          message: `${attachedCount} file(s) attached successfully.`,
        });
        
        // Close modal on success
        onClose();
      } else {
        // Handle backend error (status_code 40X in response body)
        const errorMessage = response.msg_code?.[0]?.description || 'Error attaching files.';
        toast.error({
          title: 'Error',
          message: errorMessage,
        });
      }
    } catch (error) {
      // Handle network errors or other exceptions
      toast.error({
        title: 'Error',
        message: error.message || 'An error occurred while attaching files.',
      });
    } finally {
      setIsProcessing(false);
    }
  }, [selectedDocumentUuids, selectedCount, isProcessing, findDocumentInTree, toast, onClose]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="medium" width={modalWidth}>
      <Modal.Header>
        <Modal.Header.Title>
          AIMS Drive
        </Modal.Header.Title>
      </Modal.Header>
      <Modal.Body>
        <ModalBodyContent>
          {isInitialLoading ? (
            <LoadingContainer>
              <CircularProgress />
            </LoadingContainer>
          ) : (
            <FileExplorerContainer>
              {treeData.root ? (
                renderTree()
              ) : (
                <EmptyStateMessage>
                  No folders or documents found
                </EmptyStateMessage>
              )}
            </FileExplorerContainer>
          )}
        </ModalBodyContent>
      </Modal.Body>
      <Modal.Actions align="end">
        <Modal.Button
          label="Cancel"
          onClick={onClose}
          type="tertiary"
        />
        <Modal.Button
          label={isProcessing ? 'Processing...' : `Process Selected Files (${selectedCount})`}
          onClick={handleProcessFiles}
          type="primary"
          disabled={selectedCount === 0 || isProcessing}
        />
      </Modal.Actions>
    </Modal>
  );
};

export default AIMSDriveModal;
